import { useEffect, useRef, useState } from "react";
import { Button, Input } from "@lumika/ui";
import { authApi } from "../api/auth";

const OTP_LEN = 8;

type Stage = "email" | "code";

type Props = {
  onSuccess: (kind: "session" | "choice") => void;
};

/**
 * Two-stage passwordless OTP form:
 *   1. Email stage — user enters email, clicks "Email me a code".
 *   2. Code stage — 8 single-digit cells; auto-verifies the instant the
 *      8th digit lands (fired exactly once via a `verifying` guard).
 *
 * On success the parent routes by `kind`; on failure cells clear,
 * inline error shows, cell 0 refocuses.
 */
export function EmailCodeForm({ onSuccess }: Props) {
  const [stage, setStage] = useState<Stage>("email");
  const [email, setEmail] = useState("");
  const [cells, setCells] = useState<string[]>(Array(OTP_LEN).fill(""));
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  const cellRefs = useRef<(HTMLInputElement | null)[]>([]);
  const cooldownTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (cooldownTimer.current) window.clearInterval(cooldownTimer.current);
    };
  }, []);

  function startCooldown() {
    setResendCooldown(30);
    cooldownTimer.current = window.setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          window.clearInterval(cooldownTimer.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await authApi.requestEmailCode(email.trim());
      setStage("code");
      setCells(Array(OTP_LEN).fill(""));
      startCooldown();
      // Focus first cell after render.
      setTimeout(() => cellRefs.current[0]?.focus(), 0);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  }

  async function submitOtp(value: string) {
    if (verifying) return;
    setVerifying(true);
    setError(null);
    try {
      const result = await authApi.verifyEmailCode(email.trim(), value);
      onSuccess(result.kind);
    } catch {
      setError("That code is incorrect or has expired. Please try again.");
      setCells(Array(OTP_LEN).fill(""));
      setVerifying(false);
      setTimeout(() => cellRefs.current[0]?.focus(), 0);
    }
  }

  function setCell(i: number, ch: string) {
    if (verifying) return;
    const digit = ch.replace(/\D/g, "").slice(0, 1);
    const next = cells.map((c, idx) => (idx === i ? digit : c));
    setCells(next);
    if (digit && i < OTP_LEN - 1) {
      cellRefs.current[i + 1]?.focus();
    }
    const value = next.join("");
    if (value.length === OTP_LEN && /^\d{8}$/.test(value)) {
      void submitOtp(value);
    }
  }

  function handleKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !cells[i] && i > 0) {
      cellRefs.current[i - 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    if (verifying) return;
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LEN);
    const next = Array(OTP_LEN)
      .fill("")
      .map((_, idx) => digits[idx] ?? "");
    setCells(next);
    const value = next.join("");
    if (value.length === OTP_LEN && /^\d{8}$/.test(value)) {
      void submitOtp(value);
    } else {
      const focus = Math.min(digits.length, OTP_LEN - 1);
      cellRefs.current[focus]?.focus();
    }
  }

  async function handleResend() {
    if (resendCooldown > 0 || sending) return;
    setSending(true);
    setError(null);
    try {
      await authApi.requestEmailCode(email.trim());
      setCells(Array(OTP_LEN).fill(""));
      startCooldown();
      setTimeout(() => cellRefs.current[0]?.focus(), 0);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  }

  if (stage === "email") {
    return (
      <form onSubmit={handleRequestCode} className="flex w-full flex-col gap-3">
        <Input
          type="email"
          autoComplete="email"
          required
          aria-label="Work email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full"
        />
        {error && (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={sending}>
          {sending ? "Sending…" : "Email me a code"}
        </Button>
      </form>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        We emailed an 8-digit code to <span className="font-medium text-foreground">{email}</span>.
      </p>

      <div className="flex gap-1.5" onPaste={handlePaste} aria-label="One-time code">
        {cells.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              cellRefs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={digit}
            disabled={verifying}
            onChange={(e) => setCell(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            className="h-12 min-w-0 flex-1 rounded-md border border-input bg-background text-center text-lg font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-ring/25 disabled:opacity-50"
            aria-label={`Digit ${i + 1}`}
          />
        ))}
      </div>

      {verifying && (
        <p className="text-xs text-muted-foreground" aria-live="polite">
          Verifying…
        </p>
      )}

      {error && !verifying && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}

      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <button
          type="button"
          onClick={handleResend}
          disabled={resendCooldown > 0 || sending || verifying}
          className="underline hover:no-underline disabled:cursor-not-allowed disabled:opacity-50 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
        </button>
        <span>·</span>
        <button
          type="button"
          onClick={() => {
            setStage("email");
            setCells(Array(OTP_LEN).fill(""));
            setError(null);
            setVerifying(false);
          }}
          className="underline hover:no-underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Use a different email
        </button>
      </div>
    </div>
  );
}
